import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

// Ayarlar → Yetkilendirme: rol × yetki tablosu (sadece admin). Admin sütunu kilitli: admin her şeye yetkilidir.
const ROLE_LABELS = {
  admin: 'Admin', finance: 'Muhasebe', operations: 'Operasyon', sales: 'Satış',
  account_manager: 'Account Manager', ppc_manager: 'PPC Manager', viewer: 'İzleyici',
};

export default function PermissionsEditor() {
  const { refreshPermissions } = useAuth();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/permissions').then(r => { setData(r.data); setDraft(JSON.parse(JSON.stringify(r.data.matrix))); })
      .catch(err => setError(err.response?.data?.error || 'Yetkiler yüklenemedi'));
  }, []);

  const groups = useMemo(() => {
    if (!data) return [];
    const g = [];
    for (const c of data.catalog) {
      if (!g.length || g[g.length - 1].name !== c.group) g.push({ name: c.group, items: [] });
      g[g.length - 1].items.push(c);
    }
    return g;
  }, [data]);

  if (error) return <div className="card text-muted">{error}</div>;
  if (!data || !draft) return <div className="page-loading">Yükleniyor...</div>;

  const changed = data.roles.flatMap(r => data.catalog.filter(c => draft[r][c.key] !== data.matrix[r][c.key]).map(c => `${r}|${c.key}`));
  const toggle = (role, key) => setDraft(d => ({ ...d, [role]: { ...d[role], [key]: !d[role][key] } }));
  const isDefault = (role, c) => draft[role][c.key] === c.default_roles.includes(role);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/permissions', { matrix: draft });
      setData(d => ({ ...d, matrix: r.data.matrix }));
      setDraft(JSON.parse(JSON.stringify(r.data.matrix)));
      refreshPermissions();
      toast.success('Yetkiler kaydedildi; kullanıcılar en geç 30 saniye içinde yeni yetkileriyle çalışır');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  return (
    <div className="card perm">
      <div className="perm__head">
        <div>
          <h3 className="card__title" style={{ margin: 0 }}>Rol yetkileri</h3>
          <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
            Her rolün CRM'de neyi görüp yapabileceği. Admin her zaman tam yetkilidir. Varsayılandan farklı ayarlar işaretlenir.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn--secondary" disabled={!changed.length || saving}
            onClick={() => setDraft(JSON.parse(JSON.stringify(data.matrix)))}>Vazgeç</button>
          <button type="button" className="btn btn--primary" disabled={!changed.length || saving} onClick={save}>
            {saving ? 'Kaydediliyor…' : changed.length ? `Kaydet (${changed.length} değişiklik)` : 'Kaydet'}
          </button>
        </div>
      </div>
      <div className="perm__scroll">
        <table className="perm__table">
          <thead>
            <tr>
              <th className="perm__label">Yetki</th>
              {data.roles.map(r => <th key={r}>{ROLE_LABELS[r] || r}</th>)}
            </tr>
          </thead>
          <tbody>
            {groups.map(g => (
              <React.Fragment key={g.name}>
                <tr className="perm__group"><td colSpan={data.roles.length + 1}>{g.name}</td></tr>
                {g.items.map(c => (
                  <tr key={c.key}>
                    <td className="perm__label">{c.label}</td>
                    {data.roles.map(r => (
                      <td key={r} className={!isDefault(r, c) ? 'is-custom' : ''}>
                        <input type="checkbox" checked={!!draft[r][c.key]} disabled={r === 'admin'}
                          onChange={() => toggle(r, c.key)}
                          aria-label={`${ROLE_LABELS[r] || r}: ${c.label}`}
                          title={r === 'admin' ? 'Admin her zaman yetkilidir' : (!isDefault(r, c) ? 'Varsayılandan farklı' : '')} />
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
